import { useState, useRef, useCallback } from 'react';

/**
 * Hook for tracking unsaved changes and managing a confirmation modal.
 *
 * Usage:
 *   const { isDirty, captureInitialState, showUnsavedModal, setShowUnsavedModal, handleCloseAttempt } = useUnsavedChanges(formData);
 *
 * Call captureInitialState(data) when the form is initialized/reset.
 * Call handleCloseAttempt() when the user tries to close via backdrop/X -- returns true if blocked (dirty).
 *
 * @param {*} currentFormData - The current form state to compare against the initial snapshot
 */
export function useUnsavedChanges(currentFormData) {
    const initialRef = useRef(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    const captureInitialState = useCallback((data) => {
        initialRef.current = JSON.stringify(data);
    }, []);

    const isDirty = initialRef.current !== null
        && JSON.stringify(currentFormData) !== initialRef.current;

    const handleCloseAttempt = useCallback(() => {
        if (initialRef.current !== null && JSON.stringify(currentFormData) !== initialRef.current) {
            setShowUnsavedModal(true);
            return true;
        }
        return false;
    }, [currentFormData]);

    return {
        isDirty,
        captureInitialState,
        showUnsavedModal,
        setShowUnsavedModal,
        handleCloseAttempt
    };
}
